import { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { Container, Grid, Divider, Card, Collapse } from '@material-ui/core';

import Header from 'components/Header';
import OrderSummary from 'components/OrderSummary';
import ShipmentSummary from 'components/ShipmentSummary';
import ValidatedTextField from 'components/ValidatedTextField';
import EasyshipCheckbox from 'components/EasyshipCheckbox';
import Button from 'components/Button';
import FooterLinks from 'components/FooterLinks';
import AlertNotification from 'components/AlertNotification';

import { ClaimType, Policy } from 'services/claim/types';
import ClaimService from 'services/claim';
import { useStyles } from './styles';

interface ILostScreenState {
  policy?: Policy;
}

export default function LostScreen() {
  const classes = useStyles();
  const history = useHistory();
  const intl = useIntl();
  const { policy } = (useLocation().state || {}) as ILostScreenState;

  const [description, setDescription] = useState('');
  const [checkCertify, setCheckCertify] = useState(false);
  const [showingValidationErrors, setShowingValidationErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!policy) {
    history.push('/');
    return null;
  }

  function handleBackClick() {
    history.push('/claim', { policy });
  }

  async function handleNextClick() {
    if (submitting) return;
    if (!policy || !checkCertify || !description) {
      setShowingValidationErrors(true);
      return;
    }

    setSubmitting(true);

    const payload = {
      id: policy.claim?.id,
      claim_type: ClaimType.Lost,
      user_description: description,
      check_certify: true,
    };

    ClaimService.submit(policy.shipment.id, payload)
      .then(({ claim }) => {
        history.push('/claim/claim-summary', { policy: { ...policy, claim }, showSuccessBanner: true });
      })
      .catch((error) => {
        error === 'Token is expired' ? setErrorMessage('expired-session') : setErrorMessage('default-error');
      })
      .finally(() => setSubmitting(false));
  }

  return (
    <Container className={classes.root}>
      <Header totalSteps={3} currentStep={3}>
        <FormattedMessage id="lost.headline" defaultMessage="Claim for Lost Shipment" />
      </Header>

      <Card className={classes.summaryCard}>
        <OrderSummary
          orderNumber={policy.shipment.platform_order_number}
          orderDate={policy.shipment.order_created_at}
          imgUrl={policy.company.logo_url}
          companyName={policy.company.name}
        />
        <Divider />
        <ShipmentSummary policy={policy} />
        <Divider />
        <ValidatedTextField
          value={description}
          esChange={({ value }) => setDescription(value)}
          multiline={true}
          inputProps={{ maxLength: 1000 }}
          placeholder={intl.formatMessage({
            id: 'lost.textfield-placeholder',
            defaultMessage: 'Describe the issue to help your insurance provider evaluate the claim.',
          })}
          error={!description && showingValidationErrors}
          className={classes.textField}
        />
        <EasyshipCheckbox
          label={intl.formatMessage({
            id: 'lost.checkbox-label',
            defaultMessage: 'I certify that the information above is correct and truthful.',
          })}
          checked={checkCertify}
          onChange={() => setCheckCertify(!checkCertify)}
          error={!checkCertify && showingValidationErrors}
        />

        <Collapse in={showingValidationErrors} timeout={500}>
          <AlertNotification type="warning">
            <FormattedMessage id="global.checkbox-error-msg" defaultMessage="Please fill in the required fields." />
          </AlertNotification>
        </Collapse>

        {errorMessage === 'expired-session' && (
          <Grid item style={{ marginTop: 15 }}>
            <AlertNotification type="warning">
              <FormattedMessage
                id="global.expired-session"
                defaultMessage="This session has expired. Please refresh the page and and enter claim details to continue processing this claim."
              />
            </AlertNotification>
          </Grid>
        )}

        {errorMessage === 'default-error' && (
          <Grid item style={{ marginTop: 15 }}>
            <AlertNotification type="warning">
              <FormattedMessage
                id="global.default-error"
                defaultMessage="Something went wrong. Please refresh the page and try again."
              />
            </AlertNotification>
          </Grid>
        )}

        <Grid container direction="row" justifyContent="space-between" className={classes.buttonsContainer}>
          <Grid item>
            <Button color="default" onClick={handleBackClick}>
              <FormattedMessage id="global.back" defaultMessage="Back"></FormattedMessage>
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" onClick={handleNextClick} pending={submitting}>
              <FormattedMessage id="global.next" defaultMessage="Next"></FormattedMessage>
            </Button>
          </Grid>
        </Grid>
      </Card>

      <FooterLinks companyName={policy.company.name}></FooterLinks>
    </Container>
  );
}
